/**
 * Direction arrows along a drawn route track (mirrors legacy mapv3 `mapv3-track-arrows`).
 * Samples the polyline down to a manageable number of points and computes the travel bearing at
 * each, so a MapLibre symbol layer can rotate an arrow icon to show which way the vehicle went.
 * Field names match the v2 route payload (route_data): lat, lng.
 */

export interface ArrowFeature {
  type: 'Feature'
  geometry: { type: 'Point'; coordinates: [number, number] }
  properties: { bearing: number; ts: number; speed: number }
}

export interface ArrowFeatureCollection {
  type: 'FeatureCollection'
  features: ArrowFeature[]
}

const toRad = (deg: number): number => (deg * Math.PI) / 180
const toDeg = (rad: number): number => (rad * 180) / Math.PI

/** Initial great-circle bearing a→b in degrees (0 = N, clockwise). */
export function bearingBetween(a: [number, number], b: [number, number]): number {
  const phi1 = toRad(a[1])
  const phi2 = toRad(b[1])
  const dLng = toRad(b[0] - a[0])
  const y = Math.sin(dLng) * Math.cos(phi2)
  const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(dLng)
  return (toDeg(Math.atan2(y, x)) + 360) % 360
}

interface ArrowPoint {
  lng: number
  lat: number
  ts: number
  speed: number
}

/**
 * Arrow features for a route: at most `maxArrows`, evenly sampled, each carrying the bearing to the
 * next distinct point (so the icon points the way the vehicle travelled) plus that point's time and
 * speed for the hover popup. Duplicate consecutive coordinates are skipped so bearings stay
 * meaningful. The cap mirrors legacy mapv3 (the symbol layer then declutters by zoom).
 */
export function buildRouteArrowFeatures(points: Array<any>, maxArrows = 220): ArrowFeatureCollection {
  const pts: ArrowPoint[] = (Array.isArray(points) ? points : [])
    .map((p) => ({
      lng: Number(p.lng),
      lat: Number(p.lat),
      ts: Number(p.device_timestamp ?? p.dt ?? p.timestamp) || 0,
      speed: Math.round(Number(p.basic_speed ?? p.speed ?? 0)) || 0
    }))
    .filter((p) => Number.isFinite(p.lng) && Number.isFinite(p.lat))

  const features: ArrowFeature[] = []
  if (pts.length < 2) {
    return { type: 'FeatureCollection', features }
  }

  const step = Math.max(1, Math.floor((pts.length - 1) / maxArrows))
  for (let i = 0; i + 1 < pts.length; i += step) {
    const a = pts[i]
    const b = pts[i + 1]
    if (a.lng === b.lng && a.lat === b.lat) continue
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [a.lng, a.lat] },
      properties: { bearing: bearingBetween([a.lng, a.lat], [b.lng, b.lat]), ts: a.ts, speed: a.speed }
    })
  }
  return { type: 'FeatureCollection', features }
}
